import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Lock, X, GraduationCap, School, Phone, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SliderCaptcha from '../components/SliderCaptcha';
import { supabase } from '../supabase/client';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState<'student' | 'teacher'>('student');
  const [form, setForm] = useState({ phone: '', password: '' });
  const [error, setError] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!captchaVerified) {
      setError('请先完成人机验证');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(form.phone)) {
      setError('请输入正确的手机号');
      return;
    }

    setLoading(true);
    try {
      let account = null;

      const { data: accountByMobile } = await supabase
        .from('user_accounts')
        .select('*')
        .eq('mobile', form.phone)
        .eq('role', role)
        .maybeSingle();

      if (accountByMobile) {
        account = accountByMobile;
      } else {
        const { data: accountByPhone } = await supabase
          .from('user_accounts')
          .select('*')
          .eq('mobile', `${form.phone}@${role}.meoo.local`)
          .eq('role', role)
          .maybeSingle();
        if (accountByPhone) {
          account = accountByPhone;
        }
      }

      if (!account) {
        setError('该手机号未注册' + (role === 'student' ? '学员' : '教师') + '账号，请先注册');
        return;
      }

      if (account.password_hash !== form.password) {
        setError('密码错误');
        return;
      }

      if (role === 'teacher') {
        let profile = null;

        const { data: profileByPhone } = await supabase
          .from('profiles')
          .select('audit_status, id')
          .eq('phone', form.phone)
          .eq('role', 'teacher')
          .maybeSingle();

        if (profileByPhone) {
          profile = profileByPhone;
        } else {
          const { data: profileByEmail } = await supabase
            .from('profiles')
            .select('audit_status, id')
            .eq('phone', `${form.phone}@teacher.meoo.local`)
            .eq('role', 'teacher')
            .maybeSingle();
          if (profileByEmail) {
            await supabase.from('profiles').update({ phone: form.phone }).eq('id', profileByEmail.id);
            profile = profileByEmail;
          }
        }

        if (profile?.audit_status === 'pending') {
          setError('您的账号正在审核中，请耐心等待');
          return;
        }
        if (profile?.audit_status === 'rejected') {
          setError('您的账号审核未通过，请重新注册');
          return;
        }
      }

      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: `${form.phone}@${role}.meoo.local`,
        password: form.password,
      });

      if (signInError) {
        if (signInError.message?.includes('Invalid login credentials')) {
          const { data: createData, error: createError } = await supabase.functions.invoke('user-management', {
            body: {
              action: 'create',
              username: account.username || form.phone,
              mobile: form.phone,
              password_hash: form.password,
              role: role
            }
          });

          if (createError) {
            setError('登录失败，请稍后重试');
            return;
          }

          if (createData?.error) {
            setError(createData.error);
            return;
          }

          if (createData?.success) {
            const { error: retrySignInError } = await supabase.auth.signInWithPassword({
              email: `${form.phone}@${role}.meoo.local`,
              password: form.password,
            });

            if (retrySignInError) {
              setError('登录失败，请稍后重试');
              return;
            }
          } else {
            setError('登录失败，请稍后重试');
            return;
          }
        } else {
          setError('登录失败，请稍后重试');
          return;
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('id')
          .eq('username', form.phone)
          .maybeSingle();

        if (adminData) {
          await supabase.auth.signOut();
          setError('管理员账号请使用后台登录入口');
          return;
        }

        // 检查是否有重定向目标
        const from = (location.state as { from?: string })?.from;
        if (from) {
          navigate(from);
        } else {
          navigate('/');
        }
      } else {
        navigate('/');
      }
    } catch {
      setError('登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl p-8 shadow-xl relative">
        <button onClick={() => navigate('/')} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">登录</h1>
        </div>

        <div className="flex gap-1 mb-6 justify-center">
          <button onClick={() => setRole('student')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${role === 'student' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <User className="w-4 h-4" />学员登录
          </button>
          <button onClick={() => setRole('teacher')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${role === 'teacher' ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <School className="w-4 h-4" />教师登录
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.form key={role} initial={{ opacity: 0, x: role === 'student' ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: role === 'student' ? 20 : -20 }} onSubmit={handleSubmit} className="space-y-4">
            {error && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</motion.div>}
            <div className="relative">
              <Phone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="请输入手机号" maxLength={11} />
            </div>
            <div className="relative">
              <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="请输入密码" />
            </div>
            <SliderCaptcha onVerify={setCaptchaVerified} />
            <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-medium hover:from-blue-500 hover:to-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? '登录中...' : '登录'}
            </button>
          </motion.form>
        </AnimatePresence>

        <div className="mt-6 text-center">
          <Link to="/register" className="text-blue-600 hover:text-blue-700 text-sm">还没有账号？去注册</Link>
        </div>
      </motion.div>
    </div>
  );
}
